import { create } from 'zustand'
import type { ClockDay, ClockDayItem } from '../api/showClockDays'
import type { ClockLiveActionResponse, ClockLiveProjection, ClockLiveWarning } from '../api/showClockLive'
import { sortClockDayItems } from './helpers/showClockLive'

interface ShowClockLiveState {
  stationId: number | null
  targetDayId: number | null
  day: ClockDay | null
  items: ClockDayItem[]
  itemsById: Record<number, ClockDayItem>
  projection: ClockLiveProjection | null
  warning: ClockLiveWarning | null
  stationNowEpochMs: number | null
  setTargetDay: (dayId: number | null, stationId?: number | null) => void
  setDayDetail: (day: ClockDay, items: ClockDayItem[]) => void
  setProjection: (projection: ClockLiveProjection | null) => void
  applyActionPayload: (payload: ClockLiveActionResponse) => void
  updateItem: (item: ClockDayItem) => void
  addItem: (item: ClockDayItem) => void
  setItems: (items: ClockDayItem[]) => void
  clearWarning: () => void
  setStationNowEpochMs: (epochMs: number | null) => void
  reset: () => void
}

const initialState: Omit<
  ShowClockLiveState,
  'setTargetDay' | 'setDayDetail' | 'setProjection' | 'applyActionPayload' | 'clearWarning' | 'setStationNowEpochMs' | 'reset'
> = {
  stationId: null,
  targetDayId: null,
  day: null,
  items: [],
  itemsById: {},
  projection: null,
  warning: null,
  stationNowEpochMs: null,
}

const useShowClockLiveStore = create<ShowClockLiveState>((set) => ({
  ...initialState,
  setTargetDay: (dayId, stationId) => {
    set((state) => ({
      ...state,
      targetDayId: dayId ?? null,
      stationId: stationId && stationId > 0 ? stationId : state.stationId,
    }))
  },
  setDayDetail: (day, items) => {
    const sorted = sortClockDayItems(items)
    const map: Record<number, ClockDayItem> = {}
    sorted.forEach((item) => {
      map[item.id] = { ...item }
    })
    set((state) => ({
      ...state,
      day,
      stationId: day.station_id ?? state.stationId,
      targetDayId: day.id,
      items: sorted,
      itemsById: map,
    }))
  },
  setProjection: (projection) => {
    set((state) => ({
      ...state,
      projection,
    }))
  },
  applyActionPayload: (payload) => {
    if (!payload || typeof payload !== 'object') {
      return
    }
    set((state) => {
      const targetDayId = state.targetDayId
      if (targetDayId !== null && payload.day_id !== targetDayId) {
        return state
      }

      const nextStationId = payload.station_id || state.stationId
      if (state.stationId && payload.station_id && state.stationId !== payload.station_id) {
        return state
      }

      const existing = state.itemsById[payload.day_item_id] ?? null
      const updatedItem: ClockDayItem = existing ? { ...existing, ...payload.item } : { ...payload.item }
      const itemsById = { ...state.itemsById, [updatedItem.id]: updatedItem }
      let items = state.items

      if (existing) {
        items = state.items.map((item) => (item.id === updatedItem.id ? updatedItem : item))
      } else {
        items = sortClockDayItems([...state.items, updatedItem])
      }

      const endedItem = payload.ended_item ?? null
      if (endedItem && typeof endedItem === 'object' && typeof endedItem.id === 'number' && endedItem.id > 0) {
        const endedExisting = itemsById[endedItem.id] ?? null
        const mergedEnded: ClockDayItem = endedExisting ? { ...endedExisting, ...endedItem } : { ...endedItem }
        itemsById[mergedEnded.id] = mergedEnded
        if (endedExisting) {
          items = items.map((item) => (item.id === mergedEnded.id ? mergedEnded : item))
        } else {
          items = sortClockDayItems([...items, mergedEnded])
        }
      }

      return {
        ...state,
        stationId: nextStationId ?? state.stationId,
        items,
        itemsById,
        projection: payload.projection,
        warning: payload.warning ?? null,
      }
    })
  },
  updateItem: (updatedItem) => {
    if (!updatedItem || typeof updatedItem !== 'object') {
      return
    }
    set((state) => {
      const existing = state.itemsById[updatedItem.id] ?? null
      if (!existing) {
        return state
      }

      const nextItem = { ...existing, ...updatedItem }
      const itemsById = { ...state.itemsById, [nextItem.id]: nextItem }
      const items = state.items.map((item) => (item.id === nextItem.id ? nextItem : item))

      return {
        ...state,
        items,
        itemsById,
      }
    })
  },
  addItem: (newItem) => {
    if (!newItem || typeof newItem !== 'object') {
      return
    }
    set((state) => {
      const items = sortClockDayItems([...state.items, newItem])
      const itemsById = { ...state.itemsById, [newItem.id]: newItem }
      return { ...state, items, itemsById }
    })
  },
  setItems: (updatedItems) => {
    if (!Array.isArray(updatedItems)) {
      return
    }
    set((state) => {
      const sorted = sortClockDayItems(updatedItems)
      const itemsById: Record<number, ClockDayItem> = {}
      sorted.forEach((item) => {
        itemsById[item.id] = { ...item }
      })
      return { ...state, items: sorted, itemsById }
    })
  },
  clearWarning: () =>
    set((state) => ({
      ...state,
      warning: null,
    })),
  setStationNowEpochMs: (epochMs) =>
    set((state) => ({
      ...state,
      stationNowEpochMs: Number.isFinite(epochMs ?? Number.NaN) ? Number(epochMs) : null,
    })),
  reset: () => set({ ...initialState }),
}))

export default useShowClockLiveStore
